import axios from 'axios';
import { useAuthStore } from '@store/auth';
import { useApiConfigStore } from '@store/api_config';
import { showNotification } from 'context/notification.service';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** Skip the global error toast — the call site renders its own message. */
    silenceGlobalError?: boolean;
    /** Internal: marks the one-shot retry after a token force-refresh. */
    _retry?: boolean;
  }
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL
});

// Update baseURL dynamically before each request
api.interceptors.request.use((config) => {
  const apiUrl = useApiConfigStore.getState().getApiUrl();
  config.baseURL = apiUrl;
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Add authentication token
api.interceptors.request.use(async (config) => {
  const user = useAuthStore.getState().user;
  if (user) {
    try {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch (error) {
      console.error("Failed to get Firebase token:", error);
    }
  }
  return config;
});

// Handle errors globally
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error.config;

    // 1. Unauthorized — try a token force-refresh + one retry before logging out
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const user = useAuthStore.getState().user;
      if (user) {
        try {
          const freshToken = await user.getIdToken(true);
          originalRequest.headers.Authorization = `Bearer ${freshToken}`;
          return api(originalRequest);
        } catch {
          // Force-refresh failed → user is truly unauthorized
        }
      }
      localStorage.clear();
      useAuthStore.getState().logout();
    }

    // 2. Validation errors (400) — contextual, so they stay with the call site
    //    (a form field usually wants to render them itself).
    if (status === 400) {
      console.warn("Bad request:", error.response?.data);
    }

    // 3. Everything else the user needs to know about. These used to be a bare
    //    console.log, so a failed save/send/upload looked exactly like success.
    //    Silenced with `config.silenceGlobalError = true` where a call site
    //    renders its own message.
    if (!originalRequest?.silenceGlobalError) {
      if (status === 403) {
        showNotification({
          type: "error",
          message:
            error.response?.data?.detail ||
            "Vous n'avez pas les droits nécessaires pour cette action.",
        });
      } else if (status === 409) {
        showNotification({
          type: "error",
          message:
            error.response?.data?.detail ||
            "Cette donnée a été modifiée entre-temps. Rechargez la page.",
        });
      } else if (status >= 500) {
        console.error("Server error:", error.response?.data);
        showNotification({
          type: "error",
          message: "Le serveur n'a pas pu traiter la demande. Réessayez dans un instant.",
        });
      } else if (!error.response && error.code !== "ERR_CANCELED") {
        // No response at all: network down, DNS, CORS, timeout.
        showNotification({
          type: "error",
          message: "Connexion au serveur impossible. Vérifiez votre connexion internet.",
        });
      }
    }

    return Promise.reject(error);
  }
);

export default api;
